// dialogs/crawler-dialog/crawler-dialog.forms.ts
//
// Strongly-typed reactive form shapes and builders for CrawlerDialogComponent.
// The root group holds the common fields plus exactly one per-mode sub-group
// (single | excel | discovery), attached/detached when crawlerMode changes —
// hence those three keys are optional on CrawlerForm so addControl/removeControl
// type-check.

import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  CrawlerDiscoveryStrategyType,
  CrawlerRecurrence,
  MAX_DISCOVERY_DEPTH,
  MAX_PAGES_DEFAULT,
  MAX_PAGES_OPTIONS_MAX,
  MAX_PAGES_OPTIONS_MIN,
} from 'src/app/models/integration/crawler';
import { Trademark } from 'src/app/models/trademark';
import { CrawlerMode } from 'src/app/services/integration.service';

export type ScreenshotFormat = 'jpeg' | 'png';

// ── Per-mode sub-group shapes ──────────────────────────────────────────────

export interface SingleForm {
  url: FormControl<string>;
}

export interface ExcelForm {
  urlColumn: FormControl<string>;
  excelFile: FormControl<File | null>;
}

// depth is optional because it's attached/detached when the discovery strategy
// changes (AMAZON needs none), so addControl/removeControl type-check.
// countryCodes is a common root field (see CrawlerForm), not a discovery sub-field.
export interface DiscoveryForm {
  url: FormControl<string>;
  strategy: FormControl<CrawlerDiscoveryStrategyType>;
  depth?: FormControl<number>;
  maxPages: FormControl<number>;
}

// ── Config sub-group shape ─────────────────────────────────────────────────

export interface ViewportForm {
  width: FormControl<number>;
  height: FormControl<number>;
}

export interface ConfigForm {
  screenshotFormat: FormControl<ScreenshotFormat>;
  quality: FormControl<number>;
  viewport: FormGroup<ViewportForm>;
  concurrency: FormControl<number>;
  delay: FormControl<number>;
  batchSize: FormControl<number>;
  pageTimeout: FormControl<number>;
}

// ── Root form shape ────────────────────────────────────────────────────────

export interface CrawlerForm {
  name: FormControl<string>;
  crawlerMode: FormControl<CrawlerMode>;
  recurrence: FormControl<CrawlerRecurrence>;
  trademarks: FormControl<Trademark[]>;
  // Common to all crawler types; only DISCOVERY expands + uses them server-side.
  countryCodes: FormControl<string[]>;
  config: FormGroup<ConfigForm>;
  single?: FormGroup<SingleForm>;
  excel?: FormGroup<ExcelForm>;
  discovery?: FormGroup<DiscoveryForm>;
}

export type CrawlerFormGroup = FormGroup<CrawlerForm>;

// Builds the typed groups. `urlValidator` is injected because URL validation
// lives in UtilsService; `mode` toggles the excel-file required rule (only
// required when creating — see CRAWLER_API_MIGRATION.md).
export class CrawlerFormFactory {
  constructor(
    private readonly urlValidator: ValidatorFn,
    private readonly mode: 'create' | 'edit',
  ) {}

  root(): CrawlerFormGroup {
    return new FormGroup<CrawlerForm>({
      name: new FormControl('', { nonNullable: true, validators: Validators.required }),
      crawlerMode: new FormControl<CrawlerMode>('single', { nonNullable: true }),
      recurrence: new FormControl<CrawlerRecurrence>('once', {
        nonNullable: true,
        validators: Validators.required,
      }),
      trademarks: new FormControl<Trademark[]>([], { nonNullable: true }),
      countryCodes: new FormControl<string[]>([], { nonNullable: true }),
      config: this.config(),
    });
  }

  config(): FormGroup<ConfigForm> {
    return new FormGroup<ConfigForm>({
      screenshotFormat: new FormControl<ScreenshotFormat>('jpeg', { nonNullable: true }),
      quality: new FormControl(80, {
        nonNullable: true,
        validators: [Validators.min(1), Validators.max(100)],
      }),
      viewport: new FormGroup<ViewportForm>({
        width: new FormControl(1280, {
          nonNullable: true,
          validators: [Validators.min(320), Validators.max(3840)],
        }),
        height: new FormControl(720, {
          nonNullable: true,
          validators: [Validators.min(240), Validators.max(2160)],
        }),
      }),
      concurrency: new FormControl(2, {
        nonNullable: true,
        validators: [Validators.min(1), Validators.max(5)],
      }),
      delay: new FormControl(2000, { nonNullable: true, validators: Validators.min(0) }),
      batchSize: new FormControl(50, {
        nonNullable: true,
        validators: [Validators.min(10), Validators.max(200)],
      }),
      pageTimeout: new FormControl(240000, {
        nonNullable: true,
        validators: [Validators.min(5000), Validators.max(240000)],
      }),
    });
  }

  single(): FormGroup<SingleForm> {
    return new FormGroup<SingleForm>({
      url: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, this.urlValidator],
      }),
    });
  }

  excel(): FormGroup<ExcelForm> {
    return new FormGroup<ExcelForm>({
      urlColumn: new FormControl('', { nonNullable: true, validators: Validators.required }),
      excelFile: new FormControl<File | null>(
        null,
        this.mode === 'create' ? Validators.required : [],
      ),
    });
  }

  discovery(): FormGroup<DiscoveryForm> {
    return new FormGroup<DiscoveryForm>({
      url: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, this.urlValidator],
      }),
      strategy: new FormControl<CrawlerDiscoveryStrategyType>('GENERIC', { nonNullable: true }),
      depth: this.discoveryDepth(),
      maxPages: this.maxPages(),
    });
  }

  // Attaches/detaches the depth control to match the strategy:
  // AMAZON needs no depth, so it's removed from the group entirely.
  syncDiscoveryStrategyControls(
    group: FormGroup<DiscoveryForm>,
    strategy: CrawlerDiscoveryStrategyType,
  ): void {
    if (strategy === 'AMAZON') {
      if (group.contains('depth')) group.removeControl('depth');
    } else {
      if (!group.contains('depth')) group.addControl('depth', this.discoveryDepth());
    }
  }

  private discoveryDepth(): FormControl<number> {
    return new FormControl(1, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(MAX_DISCOVERY_DEPTH)],
    });
  }

  private maxPages(): FormControl<number> {
    return new FormControl(MAX_PAGES_DEFAULT, {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.min(MAX_PAGES_OPTIONS_MIN),
        Validators.max(MAX_PAGES_OPTIONS_MAX),
      ],
    });
  }
}
